import { useState } from 'react';
import { styled } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { ModalBase } from '@components/Modal';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { TAG_COLORS } from '@constants/conversation-tags';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 400,
        '@media (max-width: 1024px)': {
            width: '100%',
        },
    },
    '& .modal-base-content': {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
});

const Swatches = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
});

const Swatch = styled(ButtonBase, {
    shouldForwardProp: prop => prop !== 'color' && prop !== 'selected',
})(({ color, selected }) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: color,
    color: '#fff',
    boxShadow: selected ? `0 0 0 2px #fff, 0 0 0 4px ${color}` : 'none',
}));

const TagEditModal = ({ tag, title, colorLabel, maxLength, onClose, onSave }) => {
    const [name, setName] = useState(tag.name);
    const [color, setColor] = useState(tag.color);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { t } = useTranslation();

    const isNameTooLong = name.trim().length > maxLength;

    const onSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSave({ id: tag.id, name: name.trim(), color });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen
            handleClose={onClose}
            title={title}
            onPrimaryAction={onSubmit}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={!name.trim() || isNameTooLong || isSubmitting}
        >
            <Input
                inputProps={{ value: name, onChange: e => setName(e.target.value) }}
                label={t('common.name')}
                fullWidth
                error={isNameTooLong}
                helperText={isNameTooLong ? t('validation.maxLength', { count: maxLength }) : null}
            />
            {colorLabel && (
                <div>
                    <Typography variant="caption" color="textSecondary">
                        {colorLabel}
                    </Typography>
                    <Swatches>
                        {TAG_COLORS.map(swatchColor => (
                            <Swatch
                                key={swatchColor}
                                type="button"
                                color={swatchColor}
                                selected={swatchColor === color}
                                onClick={() => setColor(swatchColor)}
                            >
                                {swatchColor === color && <CheckRoundedIcon sx={{ fontSize: 16 }} />}
                            </Swatch>
                        ))}
                    </Swatches>
                </div>
            )}
        </Modal>
    );
};

export default TagEditModal;
